import { useEffect } from "react"

import {
  BOTTOM_INSET_PROPERTY,
  bottomInsetRef,
  publishedBottomInset,
  startBottomEdgeInsetWriter,
} from "@/lib/bottom-edge/writer"

/**
 * Flip locally to draw the published `--bottom-inset` as a dashed red line
 * with a live px label — the quickest way to see what the probe resolves on
 * any page. Dev aid only; keep it false in commits.
 */
const SHOW_RULER = false

/**
 * Mounts the bottom-edge inset writer for the app's lifetime. Call once per
 * app root (web, embedded) — it owns `--bottom-inset` on its readers, and two
 * concurrent writers would fight over it.
 */
export function useBottomEdgeInset() {
  useEffect(() => startBottomEdgeInsetWriter(), [])

  useEffect(() => {
    if (!SHOW_RULER) return

    const ruler = document.createElement("div")
    ruler.style.cssText = `position:fixed;left:0;right:0;bottom:max(var(${BOTTOM_INSET_PROPERTY},0px),env(safe-area-inset-bottom,0px));height:0;border-top:2px dashed red;z-index:2147483647;pointer-events:none`
    const label = document.createElement("span")
    label.style.cssText =
      "position:absolute;right:8px;bottom:4px;font:11px/1.4 monospace;color:red;background:rgba(255,255,255,.85);padding:1px 5px;border-radius:4px"
    ruler.appendChild(label)

    const sync = () => {
      label.textContent = publishedBottomInset() || "unset"
    }
    // the writer sets the property on the ruler itself, as on any reader
    const observer = new MutationObserver(sync)
    observer.observe(ruler, { attributes: true, attributeFilter: ["style"] })
    document.body.appendChild(ruler)
    const unregister = bottomInsetRef(ruler)
    sync()

    return () => {
      unregister?.()
      observer.disconnect()
      ruler.remove()
    }
  }, [])
}
